/* JourneyShell — common visual frame for customer journeys.
 *
 * Under CustomerAppShell the fixed top bar is owned by the shell
 * (CustomerShellHeader: ← · Org · Cart · Settings · Profile; center title/place);
 * this component only renders in-page title/subtitle + content using
 * CUSTOMER_TAB_CONTENT_CLASS (same stack as AccountShell / More tab).
 * CustomerTopBar is not mounted in-shell — language/appearance prefs live on
 * the Settings sheet, not the header / Profile sheet.
 * Auth routes also live under CustomerAppShell so bottom nav stays mounted.
 *
 * Outside shell, CustomerTopBar is auth-only (no language/theme in header).
 *
 * Title rule: pass `title` only when this shell owns the page h1. When the
 * child screen already renders a card/page heading (auth, embedded sign-in),
 * omit `title` so we do not duplicate labels.
 *
 * Optional `headingAccessory` places the in-page title on the left and
 * controls (search/scan) on the right from `lg` up; stacks title→controls below `lg`.
 *
 * Legacy hub shortcuts ("Co chcete udělat?") were removed — destinations live
 * in bottom nav + More.
 *
 * Optional `backTo` = named hierarchical parent (never history.back).
 * Under CustomerAppShell, content-row ← is suppressed when the fixed header
 * already shows the same depth parent (`ShellDepthBackProvider`).
 *
 * `contentWidth`: role-based max-width — `wide` (shop catalog), `narrow`
 * (donation/checkout forms), `default` (shell inherits / out-of-shell
 * WIDTH + lg:max-w-none).
 */

import type { ReactNode } from 'react';
import { CustomerTopBar } from '../../app/CustomerTopBar.js';
import { useCustomerShellChrome } from '../../app/CustomerShellChromeContext.js';
import {
  CUSTOMER_CONTENT_NARROW_CLASS,
  CUSTOMER_CONTENT_WIDE_CLASS,
  CUSTOMER_CONTENT_WIDTH_CLASS,
  CUSTOMER_TAB_CONTENT_CLASS,
} from '../../app/customerTopBarStyles.js';
import { CustomerSubpageBackBar } from '../../shared/ui/CustomerSubpageBackBar.js';
import { DirectoryPageHeader } from '../../shared/ui/DirectoryPageHeader.js';
import { DIRECTORY_PAGE_STACK_CLASS } from '../../shared/ui/directoryPickerLayout.js';

export type JourneyShellContentWidth = 'default' | 'wide' | 'narrow';

interface JourneyShellProps {
  /**
   * In-page h1 when JourneyShell owns the title. Omit (or empty) when the
   * child already renders the visible heading.
   */
  title?: string;
  subtitle?: string;
  /**
   * Optional control cluster beside the in-page heading (e.g. shop search +
   * scan). Stacks under the title below `lg` so the search field keeps a
   * usable width; from `lg` up, the heading stays on the left and controls
   * on the right.
   */
  headingAccessory?: ReactNode;
  context?: 'POST_KIOSK_PRODUCT' | 'POST_KIOSK_DONATION' | 'PHONE_FIRST_PRODUCT' | 'PHONE_FIRST_DONATION';
  /** Named parent path for ←; omit to auto-resolve from the current route. `null` hides. */
  backTo?: string | null;
  /** In-flow step back when parent is not a route. */
  onBack?: () => void;
  backLabel?: string;
  backDisabled?: boolean;
  /**
   * Role-based content width. Shop catalog → `wide`; donation/checkout forms →
   * `narrow`; omit for shell-default / out-of-shell WIDTH + lg:max-w-none.
   */
  contentWidth?: JourneyShellContentWidth;
  /**
   * Grow into leftover `#main` height (`flex-1 min-h-0`) so children can fill
   * the viewport without page scroll (e.g. scan preview).
   */
  fillRemaining?: boolean;
  children: ReactNode;
}

function resolveJourneyContentWidthClass(
  contentWidth: JourneyShellContentWidth,
): string | null {
  if (contentWidth === 'wide') {
    return CUSTOMER_CONTENT_WIDE_CLASS;
  }
  if (contentWidth === 'narrow') {
    return CUSTOMER_CONTENT_NARROW_CLASS;
  }
  return null;
}

export function JourneyShell({
  title = '',
  subtitle,
  headingAccessory,
  context,
  backTo,
  onBack,
  backLabel,
  backDisabled,
  contentWidth = 'default',
  fillRemaining = false,
  children,
}: JourneyShellProps): JSX.Element {
  const inShell = useCustomerShellChrome();
  const roleWidthClass = resolveJourneyContentWidthClass(contentWidth);
  const fillClass = fillRemaining ? 'min-h-0 flex-1' : '';

  const hasSubtitle = subtitle !== undefined && subtitle.length > 0;
  const hasAccessory = headingAccessory !== undefined && headingAccessory !== null;
  const backBar =
    backTo === null && onBack === undefined ? null : (
      <CustomerSubpageBackBar
        to={backTo}
        onBack={onBack}
        label={backLabel}
        disabled={backDisabled}
        testId="customer-journey-hierarchical-back"
      />
    );
  // With accessory at lg: title stays left; search/scan grow into remaining
  // width (up to max-w-7xl ≈ 2× former max-w-2xl) when space allows.
  // In-shell titles use DirectoryPageHeader (same recipe as Favorites / Shops).
  const description = hasSubtitle ? (subtitle ?? '') : '';
  let pageHeading: JSX.Element | null = null;
  if (title.length > 0 || hasSubtitle) {
    const header = (
      <DirectoryPageHeader
        title={title}
        description={description}
        testId="customer-journey-page-header"
      />
    );
    pageHeading = hasAccessory ? (
      <div className="min-w-0 shrink-0 lg:max-w-[min(100%,18rem)]">{header}</div>
    ) : (
      header
    );
  }
  const pageHeader = (() => {
    if (!hasAccessory) {
      return pageHeading;
    }
    return (
      <div className="flex w-full min-w-0 flex-col gap-3 lg:flex-row lg:items-end lg:gap-4">
        {pageHeading}
        <div className="min-w-0 w-full lg:min-w-[min(100%,28rem)] lg:flex-1 lg:max-w-7xl">
          {headingAccessory}
        </div>
      </div>
    );
  })();

  if (inShell) {
    // Same outer stack as Favorites / Shops (`pt-4` = safe-area-x left inset).
    // Keep CUSTOMER_TAB_CONTENT only out-of-shell / AccountShell where py-4 is owned once.
    const inShellClass =
      roleWidthClass !== null
        ? `${DIRECTORY_PAGE_STACK_CLASS} ${roleWidthClass} ${fillClass}`.trim()
        : `${DIRECTORY_PAGE_STACK_CLASS} ${fillClass}`.trim();
    return (
      <div className={inShellClass}>
        {backBar !== null ? <div className="shrink-0">{backBar}</div> : null}
        {pageHeader !== null && pageHeader !== undefined ? pageHeader : null}
        {children}
      </div>
    );
  }

  const outOfShellWidth =
    roleWidthClass ?? `${CUSTOMER_CONTENT_WIDTH_CLASS} lg:max-w-none`;

  return (
    <div className={`min-h-screen bg-[var(--color-surface-muted)] ${fillRemaining ? 'flex flex-col' : ''}`.trim()}>
      <CustomerTopBar
        title={title.length > 0 ? title : undefined}
        subtitle={subtitle}
        context={context}
      />
      <main
        className={`customer-safe-area-x customer-safe-area-bottom ${CUSTOMER_TAB_CONTENT_CLASS} ${outOfShellWidth} ${fillClass}`.trim()}
      >
        {backBar}
        {/* Title lives in CustomerTopBar out-of-shell; accessory stays in-flow. */}
        {hasAccessory ? (
          <div className="min-w-0 w-full lg:max-w-7xl">{headingAccessory}</div>
        ) : null}
        {children}
      </main>
    </div>
  );
}
